import {InstrumentTrack} from "../models/InstrumentTrack";
import { isStringInstrument } from "../lib/definitions/InstrumentTrackAnnotation";

const STRING_ARTICULATION_FIELDS = [
  'String Articulation Techniques',
  'string_articulation_techniques',
  'String_Articulation_Techniques',
  'stringArticulationTechniques'
];

/**
 * Service for handling track management operations
 */
export class TrackManagementService {
  constructor(annotationDataService) {
    this.dataService = annotationDataService;
  }

  generateNextIndex(trackName) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    const trackIdPrefix = trackName.concat("-track");
    const tracks = annotationSheet.instrument_tracks;

    let newTrackInstanceIdx = -1;
    const sameInstrumentTracks = tracks.filter(t => t.track_id.includes(trackIdPrefix));
    for (let i = 0; i <= sameInstrumentTracks.length; i++) {
      if (sameInstrumentTracks.length === 0 || sameInstrumentTracks.findIndex(t => t.track_id === trackIdPrefix.concat("_").concat(i.toString())) < 0) {
        newTrackInstanceIdx = i;
        // console.log(trackIdPrefix.concat("_").concat(i.toString()));
        break;
      }
    }
    if (newTrackInstanceIdx === -1) {
      console.error("Track Index is not calculated correctly");
    }
    return newTrackInstanceIdx;
  }

  // create a new track
  createTrack(track) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (annotationSheet?.instrument_tracks && annotationSheet?.instrument_tracks.findIndex(t => t.track_id === track.track_id) >= 0) {
      // cannot have two identical tracks
      console.error("The track with provided id already exists - ", track.track_id);
      return false;
    }
    const newTrack = new InstrumentTrack(track);
    if (!this.dataService.annotationBlockStore.addTrack(track.track_id)) return false;
    annotationSheet.instrument_tracks.push(newTrack);
    this.dataService.notifyListeners();
    return track.track_id;
  }

  removeTrack(trackId) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (!annotationSheet.instrument_tracks.some(track => track.track_id === trackId)) return false;
    if (!this.dataService.annotationBlockStore.removeTrack(trackId)) return false;
    annotationSheet.instrument_tracks = annotationSheet.instrument_tracks.filter(t => t.track_id !== trackId);
    this.dataService.notifyListeners();
    return true;
  }

  reorderTracks(activeId, overId) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    const tracks = annotationSheet.instrument_tracks;
    const oldIndex = tracks.findIndex(t => t.track_id === activeId);
    const newIndex = tracks.findIndex(t => t.track_id === overId);
    if (oldIndex < 0 || newIndex < 0) return;
    const [moved] = tracks.splice(oldIndex, 1);
    tracks.splice(newIndex, 0, moved);
    this.dataService.notifyListeners();
  }

  clearChordLabelingSystem() {
    const annotationSheet = this.dataService.getAnnotationSheet();
    annotationSheet.labeling_system = "";
  }

  /**
   * Rename track with proper data clearing and persistence
   */
  async renameTrack(trackId, newTrackId, newTrackName) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    if (typeof newTrackId !== 'string' || newTrackId.trim() === '') return false;
    const targetTrack = annotationSheet.instrument_tracks.find(track => track.track_id === trackId);
    if (!targetTrack) return false;
    if (trackId !== newTrackId && annotationSheet.instrument_tracks.some(track => track.track_id === newTrackId)) {
      return false;
    }

    const oldIsString = isStringInstrument(targetTrack.name || '');
    const newIsString = isStringInstrument(newTrackName);
    const instrumentTypeChanged = oldIsString !== newIsString;

    if (!this.dataService.annotationBlockStore.renameTrack(trackId, newTrackId)) return false;
    if (oldIsString && !newIsString) {
      this.dataService.annotationBlockStore.transformTrack(newTrackId, block => {
        const nextBlock = { ...block };
        STRING_ARTICULATION_FIELDS.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(nextBlock, field)) nextBlock[field] = [];
        });
        const formData = { ...(block.formData || {}) };
        STRING_ARTICULATION_FIELDS.forEach(field => {
          if (Object.prototype.hasOwnProperty.call(formData, field)) formData[field] = [];
        });
        return { ...nextBlock, formData };
      });
    } else if (!oldIsString && newIsString) {
      this.dataService.annotationBlockStore.transformTrack(newTrackId, block => ({
        ...block,
        formData: { ...(block.formData || {}), completed: false }
      }));
    }

    targetTrack.track_id = newTrackId;
    targetTrack.name = newTrackName;
    targetTrack.instrument = newTrackName;
    this.dataService.notifyListeners();

    if (instrumentTypeChanged) {
      try {
        await this.dataService.saveAnnotationSheet(
          this.dataService.annotationSheet?.general_info?.user_id,
          this.dataService.sheet_id,
          this.dataService.session_id
        );
      } catch (error) {
        console.error('TrackManagementService: Failed to save transformed data:', error);
      }
    }
    return newTrackId;
  }

  markTrackForDeletion(trackId, marked = true) {
    const annotationSheet = this.dataService.getAnnotationSheet();
    const targetTrack = annotationSheet.instrument_tracks.find(track => track.track_id === trackId);
    if (!targetTrack) return false;

    const newName = marked ? 'Delete' : 'Unknown';
    const newTrackId = `${newName}-track_${this.generateNextIndex(newName)}`;
    if (!this.dataService.annotationBlockStore.clearAndRenameTrack(trackId, newTrackId)) return false;

    targetTrack.track_id = newTrackId;
    targetTrack.name = newName;
    targetTrack.is_main_melody = false;
    targetTrack.marked_for_deletion = marked;
    this.dataService.notifyListeners();
    return newTrackId;
  }
}
